// 전역 핸들러·catch 에 뭐가 들어올지 모르므로(AxiosError · CommonError · Error · 문자열 · undefined)
// 여기서 한 번 AppError 로 정규화한다. 뒤의 파이프라인은 "항상 AppError" 만 가정한다.

import { type AxiosError, type AxiosRequestConfig, isAxiosError } from 'axios'

import {
  AppError,
  type ErrorFlow,
  type ErrorKind,
  type ErrorMechanism,
  type ErrorSeverity,
  isAppError,
} from './AppError'
import { CANCEL_ERROR_NAMES, CANCELED_CODE } from './policy'
import { toRoute } from './route'

export type ToAppErrorOptions = {
  mechanism?: ErrorMechanism
  // 던지는 객체에 flow 를 실을 수 없을 때(날것 AxiosError) 호출부가 컨텍스트로 넘긴다. 객체에 이미 있으면 그쪽이 우선.
  flow?: ErrorFlow
  routeOverrides?: Record<string, string>
}

const KINDS: readonly ErrorKind[] = ['API', 'client', 'network']

// 폴백 화면·부팅 실패는 사용자가 더 진행할 수 없다. 전역 핸들러까지 올라온 것은 화면이 살아 있을 수 있어 올리지 않는다.
const SCREEN_DEAD_MECHANISMS: readonly ErrorMechanism[] = ['boundary', 'boot']

const clientSeverity = (mechanism: ErrorMechanism | undefined): ErrorSeverity =>
  mechanism && SCREEN_DEAD_MECHANISMS.includes(mechanism) ? 'fatal' : 'error'

type CommonErrorLike = Error & {
  type?: ErrorKind
  status?: number
  code?: string
  config?: AxiosRequestConfig
  flow?: ErrorFlow
}

// DOMException 은 환경(jsdom·일부 브라우저)에 따라 Error 의 instanceof 체인에 안 걸리므로
// name·message 를 가진 모양으로 판별한다.
const isErrorLike = (value: unknown): value is Error =>
  value instanceof Error ||
  (typeof value === 'object' &&
    value !== null &&
    typeof (value as { name?: unknown }).name === 'string' &&
    typeof (value as { message?: unknown }).message === 'string')

// shared 는 선생님앱의 CommonError 클래스를 import 할 수 없어 모양으로 판별한다.
const isCommonErrorLike = (value: unknown): value is CommonErrorLike =>
  isErrorLike(value) &&
  value.name === 'CommonError' &&
  KINDS.includes((value as CommonErrorLike).type as ErrorKind)

// React Query 의 CancelledError 는 name 을 바꾸지 않고(name 은 'Error') message 에만 이름을 넣는다.
// 번들러가 클래스 이름을 줄이므로 constructor.name 은 믿을 수 없다.
const isCancelError = (value: unknown): value is Error =>
  isErrorLike(value) &&
  (CANCEL_ERROR_NAMES.includes(value.name) || CANCEL_ERROR_NAMES.includes(value.message))

const buildUrlPath = (config: AxiosRequestConfig | undefined): string | undefined => {
  if (!config?.url) return undefined
  const params = config.params
  if (!params || typeof params !== 'object') return config.url
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null) continue
    query.set(key, String(value))
  }
  const serialized = query.toString()
  if (!serialized) return config.url
  return `${config.url}${config.url.includes('?') ? '&' : '?'}${serialized}`
}

const readResponseCode = (data: unknown): { code: string; message?: string } => {
  if (data && typeof data === 'object') {
    const body = data as { code?: unknown; message?: unknown }
    return {
      code: typeof body.code === 'string' ? body.code : 'UNKNOWN_CODE',
      message: typeof body.message === 'string' ? body.message : undefined,
    }
  }
  return { code: 'UNKNOWN_CODE' }
}

const requestFields = (
  config: AxiosRequestConfig | undefined,
  overrides?: Record<string, string>,
) => {
  const urlPath = buildUrlPath(config)
  return {
    method: config?.method?.toLowerCase(),
    urlPath,
    route: urlPath ? toRoute(urlPath, overrides) : undefined,
  }
}

const fromAxiosError = (error: AxiosError, options: ToAppErrorOptions): AppError => {
  const request = requestFields(error.config, options.routeOverrides)

  if (error.code === 'ERR_CANCELED') {
    return new AppError(error.message, {
      kind: 'network',
      severity: 'warning',
      code: CANCELED_CODE,
      cause: error,
      flow: options.flow,
      mechanism: options.mechanism,
      ...request,
    })
  }

  if (error.response) {
    const { status } = error.response
    const { code, message } = readResponseCode(error.response.data)
    return new AppError(message ? `${code}: ${message}` : error.message, {
      kind: 'API',
      severity: status === 429 ? 'error' : 'warning',
      code,
      status,
      cause: error,
      flow: options.flow,
      mechanism: options.mechanism,
      ...request,
    })
  }

  return new AppError(error.message, {
    kind: 'network',
    severity: 'warning',
    code: error.code ?? 'UNKNOWN',
    cause: error,
    flow: options.flow,
    mechanism: options.mechanism,
    ...request,
  })
}

const fromCommonErrorLike = (error: CommonErrorLike, options: ToAppErrorOptions): AppError => {
  const kind = error.type as ErrorKind
  const severity =
    kind === 'client'
      ? clientSeverity(options.mechanism)
      : error.status === 429
        ? 'error'
        : 'warning'
  return new AppError(error.message, {
    kind,
    severity,
    code: error.code,
    status: error.status,
    cause: error,
    flow: error.flow ?? options.flow,
    mechanism: options.mechanism,
    ...requestFields(error.config, options.routeOverrides),
  })
}

const MAX_OBJECT_MESSAGE = 200

// Error 가 아닌 객체(reject({ code, message }) 등)는 String() 이 전부 '[object Object]' 라 한 이슈로 뭉친다.
// 내용을 짧게 직렬화해 메시지로 삼는다. 어떤 값이 와도 예외가 새면 안 된다.
const stringify = (value: unknown): string => {
  try {
    if (typeof value !== 'object' || value === null) return String(value)
    const json = JSON.stringify(value)
    if (json === undefined) return String(value)
    return json.length > MAX_OBJECT_MESSAGE ? `${json.slice(0, MAX_OBJECT_MESSAGE)}…` : json
  } catch {
    return '[unserializable]'
  }
}

export const toAppError = (value: unknown, options: ToAppErrorOptions = {}): AppError => {
  if (isAppError(value)) {
    if (!value.mechanism && options.mechanism) value.mechanism = options.mechanism
    if (!value.flow && options.flow) value.flow = options.flow
    return value
  }

  if (isAxiosError(value)) return fromAxiosError(value, options)

  if (isCommonErrorLike(value)) return fromCommonErrorLike(value, options)

  if (isCancelError(value)) {
    return new AppError(value.message, {
      kind: 'network',
      severity: 'warning',
      code: CANCELED_CODE,
      cause: value,
      flow: options.flow,
      mechanism: options.mechanism,
    })
  }

  if (isErrorLike(value)) {
    return new AppError(value.message, {
      kind: 'client',
      severity: clientSeverity(options.mechanism),
      cause: value,
      flow: options.flow,
      mechanism: options.mechanism,
    })
  }

  // Error 가 아닌 값은 발생 지점을 알 수 없다. 봉투 자체의 스택(여기 toAppError)은 단서가 아니므로
  // 비워 두어 classify 가 unknown 으로 판정하게 한다.
  const wrapped = new AppError(stringify(value), {
    kind: 'client',
    severity: clientSeverity(options.mechanism),
    flow: options.flow,
    mechanism: options.mechanism,
  })
  wrapped.stack = undefined
  return wrapped
}
